import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/stores/user';

import PageOverview from '@/views/PageOverview.vue';

import mixpanel from '@/mixpanel.ts';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: '',
      meta: { auth: true },
      children: [
        {
          path: '/overview',
          name: 'overview',
          component: PageOverview,
        },
        {
          path: '/scorecard',
          name: 'scorecard',
          component: () => import('@/views/PageScorecard.vue'),
        },
      ],
    },
    {
      path: '/event-compare',
      name: 'event-compare',
      component: () => import('@/views/PageEventsCompare.vue'),
      meta: { auth: true },
    },
    {
      path: '/benchmarking',
      name: 'benchmarking',
      component: () => import('@/views/PageBenchmarking.vue'),
      meta: { auth: true, requiresFeature: ['premium_benchmarking', 'scorecard'] },
    },

    {
      path: '/echo',
      name: 'echo',
      component: () => import('@/views/PageEcho.vue'),
      meta: { auth: true, requiresFeature: ['vantage_echo'] },
    },
    {
      path: '/accounts',
      name: 'accounts',
      component: () => import('@/views/PageAccounts.vue'),
      meta: { auth: true, requiresMeshh: true },
    },
    {
      path: '/accounts/profile/:accountId/approve/:hash',
      name: 'admin-approve',
      component: () => import('@/views/PageAdminApprove.vue'),
      meta: { auth: true },
    },
    {
      path: '/api',
      name: 'api',
      component: () => import('@/views/PageAPI.vue'),
      meta: { auth: true },
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('@/views/PageContact.vue'),
      meta: { auth: true },
    },
    {
      path: '/events',
      meta: { auth: true },    
      children: [
        {
          path: '',
          name: 'events',
          component: () => import('@/views/PageEvents.vue'),
        },
        {
          path: 'new',
          name: 'event-new',
          component: () => import('@/views/PageEventWizard.vue'),
          meta: { requiresScope: 'events.create'}
        },
        {
          path: ':id',
          name: 'event-details',
          component: () => import('@/views/PageEventDetails.vue'),
          meta: { requiresScope: 'events.show'}
        },
        {
          path: ':id/edit',
          name: 'event-edit',
          component: () => import('@/views/PageEventWizard.vue'),
          meta: { requiresScope: 'events.edit'}
        },
        {
          path: ':id/duplicate',
          name: 'event-duplicate',
          component: () => import('@/views/PageEventWizard.vue'),
          meta: { requiresScope: 'events.create'}
        },
      ], 
    },
    {
      path: '/booking',
      name: 'booking',
      meta: { auth: true },
      redirect: '/booking/new',
      children: [
        {
          path: 'new',
          name: 'new-booking',
          component: () => import('@/views/PageBookingNew.vue'),
          meta: { auth: true },
        },
        {
          path: 'drafts',
          name: 'draft-bookings',
          component: () => import('@/views/PageBookingDrafts.vue'),
          meta: { auth: true },
        },
        {
          path: 'active',
          name: 'active-bookings',
          component: () => import('@/views/PageBookingActive.vue'),
          meta: { auth: true },
        },
        {
          path: 'quote-approval/:quoteId',
          name: 'quote-approval',
          component: () => import('@/views/PageQuoteApproval.vue'),
          meta: { auth: true },
        },
      ],
    },

    {
      path: '/faq',
      name: 'faq',
      component: () => import('@/views/PageFAQ.vue'),
      meta: { auth: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/PageLogin.vue'),
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import('@/views/PageLogout.vue'),
      meta: { logout: true },
    },
    {
      path: '/reset/:code/:email',
      name: 'reset',
      component: () => import('@/views/PageLogin.vue'),
      meta: { reset: true },
    },
    {
      path: '/roles',
      name: 'roles',
      component: () => import('@/views/PageRoles.vue'),
      meta: { auth: true, requiresMeshh: true },
    },
    {
      path: '/sensors',
      name: 'sensors',
      meta: { auth: true },
      redirect: '/sensors/all',
      children: [
        {
          path: 'all',
          name: 'all-sensors',
          component: () => import('@/views/PageSensors.vue'),
        },
        {
          path: 'assigned',
          name: 'assigned-sensors',
          component: () => import('@/views/PageSensorsAssigned.vue'),
          meta: { auth: true, requiresMeshh: true },
        },
        {
          path: 'live',
          name: 'live-monitor',
          component: () => import('@/views/PageSensorsMonitor.vue'),
        },
        {
          path: 'live/:id',
          name: 'live-monitor-event',
          component: () => import('@/views/PageSensorsMonitorEvent.vue'),
        },
      ],
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('@/views/PageUsers.vue'),
      meta: { auth: true },
    },
    {
      path: '/user-profile',
      name: 'user-profile',
      component: () => import('@/views/PageUserProfile.vue'),
      meta: { auth: true },
    },

    {
      path: '/admin/profile/:accountId',
      name: 'admin',
      component: () => import('@/views/PageClientDetails.vue'),
      meta: { auth: true },
    },

    {
      path: '/admin/documents',
      meta: { auth: true },
      children: [
        {
          path: '',
          name: 'documents',
          component: () => import('@/views/PageDocuments.vue'),
        },
        {
          path: ':id',
          name: 'document',
          component: () => import('@/views/PageDocumentDetail.vue'),
        },
      ],
    },

    {
      path: '/feature-flags',
      name: 'feature-flags',
      component: () => import('@/views/PageFeatureFlags.vue'),
      meta: { auth: true, requiresMeshh: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/views/PageSettings.vue'),
      meta: { auth: true, requiresMeshh: true },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/views/PageNotFound.vue'),
      meta: { auth: true },
    },
    {
      path: '/unauthorised',
      name: 'unauthorised',
      component: () => import('@/views/PageUnauthorised.vue'),
      meta: { auth: true },
    },
  ],
});

router.beforeResolve(async (to) => {
  const userStore = useUserStore();

  if (!userStore.isLoggedIn) {
    // Restore from cookies if we can
    userStore.restoreFromCookies();
  }

  if (to.path === '/login') {
    return true;
  }

  if (to.path === '/') {
    if (userStore.userData) {
      return { path: '/overview', query: to.query };
    }
  }

  // If we don't care about login status, skip.
  if (to.meta && to.meta.auth) {
    // If we aren't logged in...
    if (!userStore.isLoggedIn) {
      return '/login';
    }

    // Gate routes by user scope
    if (to.meta.requiresScope) {
      const scope = to.meta.requiresScope as string;
      if (!userStore.hasScope(scope)) {
        return {
          path: '/unauthorised',
        };
      }
    }

    if (
      (to.meta.requiresMeshh && userStore.isNonStaff) ||
      (to.meta.requiresFeature &&
        (Array.isArray(to.meta.requiresFeature)
          ? !userStore.hasAnyFeature(to.meta.requiresFeature)
          : !userStore.hasFeature(to.meta.requiresFeature as string)))
    ) {
      if (to.meta.unauthorizedMessage) {
        return {
          path: '/unauthorised',
          query: {
            header: (to.meta.unauthorizedMessage as { header: string }).header,
            description: (to.meta.unauthorizedMessage as { description: string }).description,
          },
        };
      }

      return '/unauthorised';
    }

    // Check if token is expired or expires within 6 hours
    return await userStore.checkTokenExpiry();
  }

  return true;
});

type PageTitle = {
  [key: string]: string;
};

const pageTitles: PageTitle = {
  api: 'API',
  contact: 'Contact Us',
  faq: 'FAQ',
  reset: 'Password Reset',
};

router.afterEach((to) => {
  const name = to.name as keyof PageTitle;
  const title =
    pageTitles[name] ||
    (to.name
      ? to.name
        .toString()
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      : 'Unknown Page');

  mixpanel.track(`View ${title}`, {
    title,
    path: to.path,
  });
});

export default router;
